import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";
import { authBlobUrl, FIELD_TYPES } from "../lib/dealJacket";

export default function AdminDocumentTemplateEditor() {
  const { templateId } = useParams();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [page, setPage] = useState(1);
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageUrl, setPageUrl] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const overlayRef = useRef(null);

  useEffect(() => { if (ready && !isDealerUser(user)) navigate("/dealer-login"); }, [ready, user, navigate]);

  const load = useCallback(async () => {
    const [t, m] = await Promise.all([
      api.get(`/admin/documents/templates/${templateId}`),
      api.get("/admin/documents/mappings"),
    ]);
    setTpl(t.data.template);
    setFields(t.data.template.fields || []);
    setMappings(m.data.mappings || []);
  }, [templateId]);
  useEffect(() => { if (isDealerUser(user)) load(); }, [user, load]);

  // Load the auth-protected page image into a blob url
  useEffect(() => {
    if (!tpl) return;
    const p = (tpl.pages || []).find(x => x.page === page);
    if (!p) return;
    let revoke = null;
    authBlobUrl(p.url).then(u => { if (u) { revoke = u; setPageUrl(u); } });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [tpl, page]);

  const pageMeta = useMemo(() => (tpl?.pages || []).find(p => p.page === page), [tpl, page]);
  const pageFields = useMemo(() => fields.filter(f => (f.page || 1) === page), [fields, page]);

  // Click on the overlay to place a new field
  const onOverlayClick = (e) => {
    if (!placing || !overlayRef.current) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const id = "fld_" + Math.random().toString(36).slice(2, 12);
    const f = { id, label: "New field", type: "text", page, x, y, w: 0.20, h: 0.035,
      font_size: 12, alignment: "left", required: false, default_value: "", crm_path: "" };
    setFields(prev => [...prev, f]);
    setSelectedId(id);
    setPlacing(false);
  };

  const updateField = (id, patch) => setFields(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  const removeField = (id) => { setFields(prev => prev.filter(f => f.id !== id)); if (selectedId === id) setSelectedId(null); };
  const duplicateField = (id) => {
    const f = fields.find(x => x.id === id); if (!f) return;
    const dup = { ...f, id: "fld_" + Math.random().toString(36).slice(2, 12), x: Math.min(0.95, f.x + 0.02), y: Math.min(0.95, f.y + 0.02), label: (f.label || "") + " copy" };
    setFields(prev => [...prev, dup]); setSelectedId(dup.id);
  };

  const onDragField = (id, e) => {
    e.stopPropagation();
    const rect = overlayRef.current.getBoundingClientRect();
    const f = fields.find(x => x.id === id); if (!f) return;
    const startX = e.clientX, startY = e.clientY, startFx = f.x, startFy = f.y;
    setSelectedId(id);
    const move = (ev) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      updateField(id, { x: Math.max(0, Math.min(1 - f.w, startFx + dx)), y: Math.max(0, Math.min(1 - f.h, startFy + dy)) });
    };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };
  const onResizeField = (id, e) => {
    e.stopPropagation();
    const rect = overlayRef.current.getBoundingClientRect();
    const f = fields.find(x => x.id === id); if (!f) return;
    const startX = e.clientX, startY = e.clientY, startW = f.w, startH = f.h;
    const move = (ev) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      updateField(id, { w: Math.max(0.04, Math.min(1 - f.x, startW + dx)), h: Math.max(0.02, Math.min(1 - f.y, startH + dy)) });
    };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  const save = async (bumpVersion) => {
    setSaving(true); setStatusMsg("");
    try {
      const r = await api.put(`/admin/documents/templates/${templateId}`,
        { fields, bump_version: !!bumpVersion });
      setTpl(r.data.template);
      setStatusMsg("Saved · " + new Date().toLocaleTimeString());
    } catch (e) {
      setStatusMsg("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!tpl) return <div className="ssc-container ssc-section" data-testid="tpl-editor-loading">Loading template…</div>;
  const selected = fields.find(f => f.id === selectedId) || null;

  return (
    <div className="ssc-container" style={{ paddingTop: 18, paddingBottom: 48 }} data-testid="tpl-editor">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <Link to="/admin/documents/templates" className="text-xs text-blue-600 hover:underline">← Templates</Link>
          <h1 className="text-xl font-bold text-[#061426] mt-1" data-testid="tpl-editor-name">{tpl.name} <span className="text-sm text-gray-500 font-normal">v{tpl.version} · {tpl.folder}</span></h1>
          <p className="text-sm text-gray-500">Place editable fields on top of your uploaded form. Map each one to CRM data so the Deal Jacket can pre-fill it for every customer.</p>
        </div>
        <div className="flex gap-2 items-center">
          {statusMsg && <span className="text-xs text-gray-500">{statusMsg}</span>}
          <button className="ssc-btn-secondary" onClick={() => save(false)} disabled={saving} data-testid="tpl-save">{saving ? "Saving…" : "Save Fields"}</button>
          <button className="ssc-btn-primary" onClick={() => save(true)} disabled={saving} data-testid="tpl-save-bump">Save + Bump Version</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Canvas */}
        <div>
          {tpl.file_kind === "pdf" && (
            <AcroFormPanel tpl={tpl} mappings={mappings} reload={load} />
          )}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-500">Page</span>
            {(tpl.pages || []).map(p => (
              <button key={p.page} onClick={() => setPage(p.page)}
                className={`text-xs px-2 py-1 rounded ${page === p.page ? "bg-[#006DFF] text-white" : "bg-gray-200 text-gray-700"}`}
                data-testid={`tpl-page-${p.page}`}>{p.page}</button>
            ))}
            <div className="flex-1" />
            <button onClick={() => setPlacing(p => !p)}
              className={`text-sm px-3 py-1 rounded ${placing ? "bg-amber-500 text-white" : "bg-emerald-600 text-white"}`}
              data-testid="tpl-place-field">{placing ? "Click on form to place…" : "+ Add Field"}</button>
          </div>
          <div className="border rounded bg-white overflow-auto" style={{ maxHeight: "82vh" }}>
            <div ref={overlayRef} onClick={onOverlayClick}
              style={{ position: "relative", cursor: placing ? "crosshair" : "default" }}
              data-testid="tpl-canvas">
              {pageUrl ? <img src={pageUrl} alt="" style={{ display: "block", width: "100%", height: "auto" }} draggable={false} /> : <div className="p-10 text-center text-gray-400">Loading page…</div>}
              {pageFields.map(f => (
                <div key={f.id}
                  onMouseDown={(e) => onDragField(f.id, e)}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(f.id); }}
                  className={`absolute border-2 ${selectedId === f.id ? "border-amber-500 bg-amber-100/40" : "border-blue-500 bg-blue-100/30"} text-[10px] font-mono`}
                  style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, cursor: "move" }}
                  data-testid={`tpl-field-${f.id}`}
                  title={f.label}>
                  <div className="px-1 truncate text-blue-900">{f.label || f.type}{f.required ? " *" : ""}</div>
                  <div onMouseDown={(e) => onResizeField(f.id, e)}
                    style={{ position: "absolute", right: -6, bottom: -6, width: 12, height: 12, background: "#006DFF", border: "2px solid white", borderRadius: 2, cursor: "nwse-resize" }} />
                </div>
              ))}
              {placing && <div className="absolute inset-0 bg-emerald-500/5 pointer-events-none flex items-start justify-center pt-4"><div className="bg-emerald-700 text-white text-xs px-2 py-1 rounded">Click anywhere on the form to drop a new field</div></div>}
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">{pageFields.length} field{pageFields.length === 1 ? "" : "s"} on this page · {fields.length} total · Page size: {pageMeta?.width}×{pageMeta?.height}px</div>
        </div>

        {/* Inspector */}
        <div className="border rounded bg-white p-3 max-h-[88vh] overflow-y-auto" data-testid="tpl-inspector">
          <h3 className="font-bold text-sm mb-2">{selected ? "Field Properties" : "Fields on this page"}</h3>
          {!selected && (
            <ul className="text-sm">
              {pageFields.map(f => (
                <li key={f.id}>
                  <button className="w-full text-left py-1 hover:bg-blue-50 px-1 rounded" onClick={() => setSelectedId(f.id)}>
                    <span className="font-mono text-[10px] text-gray-400 mr-2">{f.type}</span>
                    {f.label || "(no label)"}
                    {f.crm_path ? <span className="ml-2 text-xs text-emerald-700">→ {f.crm_path}</span> : null}
                  </button>
                </li>
              ))}
              {pageFields.length === 0 && <li className="text-gray-400 text-center py-3">No fields yet. Click "+ Add Field" then click on the form.</li>}
            </ul>
          )}
          {selected && <Inspector field={selected} mappings={mappings}
            onUpdate={(patch) => updateField(selected.id, patch)}
            onDelete={() => removeField(selected.id)}
            onDup={() => duplicateField(selected.id)}
            onClose={() => setSelectedId(null)} />}
        </div>
      </div>
    </div>
  );
}

function Inspector({ field, mappings, onUpdate, onDelete, onDup, onClose }) {
  return (
    <div className="space-y-2 text-sm" data-testid="tpl-inspector-form">
      <button className="text-xs text-blue-600 hover:underline mb-1" onClick={onClose}>← all fields on this page</button>
      <L label="Label"><input className="inp" value={field.label || ""} onChange={(e) => onUpdate({ label: e.target.value })} data-testid="insp-label" /></L>
      <L label="Type">
        <select className="inp" value={field.type} onChange={(e) => onUpdate({ type: e.target.value })} data-testid="insp-type">
          {FIELD_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
      </L>
      <L label="Map to CRM">
        <select className="inp" value={field.crm_path || ""} onChange={(e) => onUpdate({ crm_path: e.target.value || null })} data-testid="insp-crm">
          <option value="">— Manual (no auto-fill) —</option>
          {mappings.map(g => (
            <optgroup key={g.group} label={g.group}>
              {g.items.map(it => <option key={it.path} value={it.path}>{it.label}</option>)}
            </optgroup>
          ))}
        </select>
      </L>
      <L label="Default value"><input className="inp" value={field.default_value || ""} onChange={(e) => onUpdate({ default_value: e.target.value })} /></L>
      {field.type === "dropdown" && (
        <L label="Options (one per line)">
          <textarea className="inp" rows={3} value={(field.options || []).join("\n")}
            onChange={(e) => onUpdate({ options: e.target.value.split("\n").filter(Boolean) })} />
        </L>
      )}
      <div className="grid grid-cols-2 gap-2">
        <L label="X %"><input className="inp" type="number" step="0.1" value={Math.round(field.x * 1000) / 10} onChange={(e) => onUpdate({ x: Math.max(0, Math.min(1, parseFloat(e.target.value || 0) / 100)) })} /></L>
        <L label="Y %"><input className="inp" type="number" step="0.1" value={Math.round(field.y * 1000) / 10} onChange={(e) => onUpdate({ y: Math.max(0, Math.min(1, parseFloat(e.target.value || 0) / 100)) })} /></L>
        <L label="W %"><input className="inp" type="number" step="0.1" value={Math.round(field.w * 1000) / 10} onChange={(e) => onUpdate({ w: Math.max(0.02, Math.min(1, parseFloat(e.target.value || 0) / 100)) })} /></L>
        <L label="H %"><input className="inp" type="number" step="0.1" value={Math.round(field.h * 1000) / 10} onChange={(e) => onUpdate({ h: Math.max(0.02, Math.min(1, parseFloat(e.target.value || 0) / 100)) })} /></L>
        <L label="Font size"><input className="inp" type="number" min={6} max={48} value={field.font_size || 12} onChange={(e) => onUpdate({ font_size: parseInt(e.target.value || 12) })} /></L>
        <L label="Align">
          <select className="inp" value={field.alignment || "left"} onChange={(e) => onUpdate({ alignment: e.target.value })}>
            <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
          </select>
        </L>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!field.required} onChange={(e) => onUpdate({ required: e.target.checked })} data-testid="insp-required" /> Required</label>
      <div className="flex gap-2 pt-2">
        <button className="ssc-btn-secondary" onClick={onDup} data-testid="insp-duplicate">Duplicate</button>
        <button className="ssc-btn-secondary text-red-600 ml-auto" onClick={onDelete} data-testid="insp-delete">Delete</button>
      </div>
      <style>{`.inp { width: 100%; border: 1px solid #cbd5e1; border-radius: 4px; padding: 4px 6px; font-size: 13px; }`}</style>
    </div>
  );
}
function L({ label, children }) {
  return <div><label className="block text-[11px] uppercase tracking-wide text-gray-500">{label}</label>{children}</div>;
}



/* Detected-AcroForm-field panel. Lets the admin (a) re-run detection
 * (b) map each PDF widget name to a CRM data path (c) save mappings.
 *
 * Only shown for PDF-kind templates. Image templates use the overlay-only
 * workflow below. PDFs MAY use both: AcroForm for the original widgets +
 * overlay fields for anything missing. */
function AcroFormPanel({ tpl, mappings, reload }) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState(() => {
    const seed = {};
    (tpl.acro_mappings || []).forEach(m => { seed[m.acro_name] = m; });
    return seed;
  });
  const [msg, setMsg] = useState("");

  const fields = tpl.acro_fields || [];
  const all = (mappings || []).flatMap(g => g.items.map(i => ({ group: g.group, ...i })));

  const onChange = (acroName, patch) => {
    setDrafts(d => ({ ...d, [acroName]: { ...(d[acroName] || { acro_name: acroName }), ...patch } }));
  };

  const detect = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post(`/admin/documents/templates/${tpl.id}/detect-acroform`);
      setMsg(`Detected ${r.data?.count || 0} AcroForm fields.`);
      reload && reload();
    } catch (e) {
      setMsg("Detection failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      // Default the type from the detected field if the admin didn't pick one.
      const payload = fields.map(f => {
        const d = drafts[f.name] || {};
        return {
          acro_name: f.name,
          type: d.type || f.type || "text",
          crm_path: d.crm_path || null,
          default_value: d.default_value || null,
          on_value: d.on_value || null,
          page: f.page,
        };
      });
      const r = await api.put(`/admin/documents/templates/${tpl.id}/acro-mappings`,
        { acro_mappings: payload });
      setMsg(`Saved ${payload.filter(p => p.crm_path).length} mappings.`);
      const seed = {};
      (r.data?.template?.acro_mappings || []).forEach(m => { seed[m.acro_name] = m; });
      setDrafts(seed);
    } catch (e) {
      setMsg("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="border rounded bg-white p-3 mb-3" data-testid="acroform-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-bold text-sm">
            AcroForm Fields
            <span className={`ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded font-bold ${fields.length > 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {fields.length > 0 ? `${fields.length} detected` : "None — overlay-only"}
            </span>
          </h3>
          <div className="text-xs text-gray-500">
            {fields.length > 0
              ? "Map each native PDF form widget to a CRM data path. Anything you don't map stays editable by hand at form-open time."
              : "This PDF has no built-in AcroForm widgets. Use the overlay editor below to draw your own fillable fields on top of the page."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={detect} disabled={busy} className="text-xs px-2 py-1 border rounded bg-white" data-testid="acroform-detect">{busy ? "…" : "Re-detect"}</button>
          {fields.length > 0 && (
            <button onClick={save} disabled={busy} className="text-xs px-3 py-1 rounded bg-blue-600 text-white font-bold" data-testid="acroform-save">{busy ? "Saving…" : "Save Mappings"}</button>
          )}
          <button onClick={() => setOpen(o => !o)} className="text-xs px-2 py-1 border rounded bg-white">{open ? "Hide" : "Show"}</button>
        </div>
      </div>
      {msg && <div className="text-xs text-gray-700 mb-2" data-testid="acroform-msg">{msg}</div>}
      {open && fields.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="acroform-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-1 font-semibold">Widget name</th>
                <th className="text-left p-1 font-semibold">Detected type</th>
                <th className="text-left p-1 font-semibold">Page</th>
                <th className="text-left p-1 font-semibold">CRM mapping</th>
                <th className="text-left p-1 font-semibold">Default / On-value</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(f => {
                const d = drafts[f.name] || {};
                return (
                  <tr key={f.name} className="border-t hover:bg-blue-50/40" data-testid={`acroform-row-${f.name}`}>
                    <td className="p-1 font-mono">{f.name}</td>
                    <td className="p-1">{f.type}{f.max_length ? ` · max ${f.max_length}` : ""}</td>
                    <td className="p-1">{f.page}</td>
                    <td className="p-1">
                      <select value={d.crm_path || ""} onChange={(e) => onChange(f.name, { crm_path: e.target.value })}
                        className="border rounded px-1 py-0.5 text-xs w-full max-w-xs"
                        data-testid={`acroform-crm-${f.name}`}>
                        <option value="">— No autofill —</option>
                        {(mappings || []).map(g => (
                          <optgroup key={g.group} label={g.group}>
                            {g.items.map(it => <option key={it.path} value={it.path}>{it.label}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td className="p-1">
                      <input value={d.default_value || ""} placeholder={f.type === "radio" ? "on-value (e.g. Yes)" : "default"}
                        onChange={(e) => onChange(f.name, { default_value: e.target.value, ...(f.type === "radio" ? { on_value: e.target.value } : {}) })}
                        className="border rounded px-1 py-0.5 text-xs w-32"
                        data-testid={`acroform-def-${f.name}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-[10px] text-gray-500 mt-1">
            {all.length} CRM mappings available across {mappings.length} groups.
          </div>
        </div>
      )}
    </div>
  );
}
